"use client";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import Button from "../Buttons/Button";
import Image from "next/image";

import getAccurateAQ from "@/assets/carousel/get-accurate-air-quality-data.webp";
import getBulkHistAQ from "@/assets/carousel/get-bulk-historical-air-quality-data.webp";
import lowAQ from "@/assets/carousel/good-air-quality-index.webp";
import moderateAQ from "@/assets/carousel/moderate-air-quality-index.webp";
import unhealthyAQ from "@/assets/carousel/unhealthy-air-quality-index.webp";
import vUnhealthyAQ from "@/assets/carousel/very-unhealthy-air-quality-index.webp";
import hazardousAQ from "@/assets/carousel/hazardous-air-quality-index.webp";
import climateMap from "@/assets/carousel/track-air-quality-and-pollen-with-climate-maps.webp";

const slidesAQ = [
  {
    profilePic: getAccurateAQ,
    alt: "Get accurate air quality data",
    label: "AQI Head",
    link: "https://www.getambee.com/api/air-quality",
    buttonText: "Access Air Quality API",
  },
  {
    profilePic: getBulkHistAQ,
    alt: "Get bulk historical air quality data",
    label: "AQI History",
    link: "https://www.getambee.com/historical/air-quality-data",
    buttonText: "Download AQ History",
  },
  {
    profilePic: climateMap,
    alt: "Track air quality and pollen with climate maps",
    label: "Climate Data",
    link: "https://maps.getambee.com/",
    buttonText: "Try Climate Map",
  },
];

const stateSlidesAQ: Record<
  string,
  { profilePic: any; label: string; alt: string }
> = {
  Low: {
    profilePic: lowAQ,
    label: "Low",
    alt: "Low air quality index",
  },
  Good: {
    profilePic: lowAQ,
    label: "Good",
    alt: "Low air quality index",
  },
  Moderate: {
    profilePic: moderateAQ,
    label: "Moderate",
    alt: "Moderate air quality index",
  },
  "Unhealthy for sensitive groups": {
    profilePic: unhealthyAQ,
    label: "Unhealthy for sensitive groups",
    alt: "Unhealthy air quality index",
  },
  Unhealthy: {
    profilePic: unhealthyAQ,
    label: "Unhealthy",
    alt: "Unhealthy air quality index",
  },
  "Very unhealthy": {
    profilePic: vUnhealthyAQ,
    label: "Very unhealthy",
    alt: "Very unhealthy air quality index",
  },
  Hazardous: {
    profilePic: hazardousAQ,
    label: "Hazardous",
    alt: "Hazardous air quality index",
  },
};

interface AnimationCarouselProps {
  page: string;
  status?: string;
  category?: string;
}

export default function AnimationCarousel({
  page,
  status = "subpage",
}: AnimationCarouselProps) {
  const isNotHomePage = page !== "home" && status;
  const containerClasses = isNotHomePage
    ? "w-[15.5rem] h-full mx-auto relative rounded-lg -mr-1 xl:-mr-4"
    : "w-[30rem] 2xl:w-[35rem] h-full mx-auto relative rounded-lg";

  if (isNotHomePage) {
    const stateSlide = stateSlidesAQ[status];
    if (!stateSlide) return null;

    return (
      <div className={containerClasses}>
        <Image src={stateSlide.profilePic} alt={stateSlide.alt || "image"} loading="eager" priority/>
      </div>
    );
  }

  return (
    <div className={containerClasses}>
      <Swiper
        modules={[Autoplay, Pagination]}
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        pagination={{ clickable: true }}
        loop
        className="custom-swiper w-full h-full bg-[#0A0E15] rounded-lg overflow-hidden relative"
      >
        {slidesAQ.map(({ profilePic, link, buttonText, alt }, index) => (
          <SwiperSlide
            key={index}
            className="relative w-full h-full flex items-center justify-center"
          >
            <Image src={profilePic} alt={alt || "image"} loading="eager" priority/>
            <Button
              text={buttonText}
              style="absolute bottom-6 right-10 bg-neutral-dark-50 px-11 lg:px-8 border-2 border-secondary-100 text-secondary-100"
              href={link}
              external
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}
